import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Divider from "@mui/material/Divider";
import _Skeleton from "@mui/material/Skeleton";
import _Typography from "@mui/material/Typography";
import _Button2 from "@mui/material/Button";
import _Box from "@mui/material/Box";
import _TextField from "@mui/material/TextField";
import _InputAdornment from "@mui/material/InputAdornment";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenge } from '../../../hooks';
import useEventRankingQuery from '../../../hooks/use-event-ranking-query';
import PodiumCard, { podiumOrder, podiumRanksConfig } from '../../event/leaderboard/podium-card';
import { GeogroupAvatar } from '../../geogroup-avatar';
import { SearchIcon } from '../../icons';
import { leaderboardSizes, leaderboardSizesMap } from '../sizes';
import LeaderboardCompanyItem from './item';
import RankCard from './rank-card';
function CompaniesLeaderboardHome({
  Button,
  event,
  ownGeogroup,
  selectLeaderboardSize,
  displayLeaderboard,
  onOwnCompanyClick,
  setSearch
}) {
  const [tmpSearch, setTmpSearch] = useState('');
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    borderRadius: 4,
    padding: 3
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    margin: "none",
    onChange: ({
      currentTarget: {
        value
      }
    }) => setTmpSearch(value),
    onKeyDown: ({
      key
    }) => {
      if (key === 'Enter') {
        setSearch(tmpSearch);
        if (tmpSearch) displayLeaderboard(true);
      }
    },
    placeholder: t('commons.challenges.leaderboard.search_company') || '',
    size: "small",
    slotProps: {
      input: {
        endAdornment: tmpSearch && /*#__PURE__*/React.createElement(_InputAdornment, {
          position: "end"
        }, /*#__PURE__*/React.createElement(_Tooltip, {
          title: "Rechercher"
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
          disabled: !tmpSearch,
          onClick: () => {
            setSearch(tmpSearch);
            if (tmpSearch) displayLeaderboard(true);
          },
          size: "small"
        }, /*#__PURE__*/React.createElement(SearchIcon, {
          fontSize: "small"
        })))))
      }
    },
    sx: {
      '.MuiInputBase-root': {
        bgcolor: '#fff'
      }
    },
    type: "text",
    value: tmpSearch,
    variant: "outlined"
  })), leaderboardSizes.map(size => /*#__PURE__*/React.createElement(Leaderboard, {
    Button: Button,
    event: event,
    key: size,
    onOwnCompanyClick: onOwnCompanyClick,
    ownGeogroup: ownGeogroup,
    selectSize: size => {
      selectLeaderboardSize(size);
      displayLeaderboard(true);
    },
    size: size
  })));
}
function Leaderboard({
  Button: _Button,
  event,
  ownGeogroup,
  size,
  selectSize,
  onOwnCompanyClick
}) {
  const {
    t
  } = useTranslation();
  const {
    getProgressValueWithUnit
  } = useChallenge();
  const _size = size === 'small' ? {
    max: 50
  } : size === 'medium' ? {
    min: 51,
    max: 500
  } : {
    min: 501
  };
  const isOwnGeogroupSize = ownGeogroup && size === ownGeogroup.companySize;
  const {
    data: topResult
  } = useEventRankingQuery(event.id, {
    page: 1,
    pageSize: 5,
    groupTypes: ['company'],
    size: _size
  });
  const leaderboard = topResult?.items;
  const count = topResult?.count;
  const {
    data: ownResult
  } = useEventRankingQuery(isOwnGeogroupSize ? event.id : undefined, {
    page: 1,
    pageSize: 1,
    groupTypes: ['company'],
    size: _size,
    groupsIds: ownGeogroup ? [ownGeogroup.id] : undefined
  });
  const ownItem = isOwnGeogroupSize ? ownResult?.items[0] ?? null : null;
  const {
    titleKey,
    descriptionKey
  } = leaderboardSizesMap[size];
  const Button = _Button || _Button2;
  if (leaderboard && leaderboard.length === 0) return /*#__PURE__*/React.createElement(React.Fragment, null);
  return /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingX: 3,
    paddingY: 8
  }, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700,
    variant: "h6"
  }, t(titleKey)), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    color: "textSecondary",
    variant: "body2"
  }, t(descriptionKey))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    maxWidth: 680,
    sx: {
      containerType: 'inline-size'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-end",
    display: {
      '@': 'none',
      '@600': 'flex'
    },
    gap: 3,
    width: "100%"
  }, podiumOrder.map(({
    itemIndex,
    rank
  }) => {
    const item = leaderboard?.[itemIndex];
    const podiumLoading = !leaderboard;
    return /*#__PURE__*/React.createElement(PodiumCard, {
      avatar: podiumLoading || item ? /*#__PURE__*/React.createElement(GeogroupAvatar, {
        geogroup: item,
        size: podiumRanksConfig[rank].avatarSize
      }) : null,
      key: rank,
      rank: rank
    }, /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      fontSize: "0.9375rem",
      fontWeight: 600,
      title: item?.title
    }, item ? item.title : podiumLoading ? /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: "100%"
    }) : '–'), /*#__PURE__*/React.createElement(_Typography, {
      fontSize: "1.125rem",
      fontWeight: 800
    }, item ? getProgressValueWithUnit(event.challengeTemplate?.type || 'bttf', item.progressValue) : podiumLoading ? /*#__PURE__*/React.createElement(_Skeleton, {
      variant: "text",
      width: 100
    }) : '–'));
  })), /*#__PURE__*/React.createElement(RankCard, {
    challengeTemplate: event.challengeTemplate,
    numberOfParticipants: count,
    progress: ownItem
  }), /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #dde0e7",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }, leaderboard ? leaderboard.map((item, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: item.id
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardCompanyItem, {
    challengeType: event.challengeTemplate?.type,
    item: item,
    onOwnCompanyClick: onOwnCompanyClick,
    ownGeogroup: ownGeogroup,
    prevItemRank: index > 0 ? leaderboard[index - 1].rank : null
  }))) : [0, 1, 2, 3, 4, 5].map(index => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(LeaderboardCompanyItem, {
    challengeType: event.challengeTemplate?.type,
    prevItemRank: null
  }))))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, null),
    onClick: () => selectSize(size),
    size: "medium",
    variant: "contained"
  }, t('commons.challenges.leaderboard.actions.see_entire_leaderboard'))));
}
export default CompaniesLeaderboardHome;